<template>
	<!-- The pill translates half its height (24px) upwards, so mt-14 (56px) keeps a visual 32px gap to the element above -->
	<div class="relative flex z-10 h-12 mt-14 -mb-6">
		<div class="absolute inset-x-0 -translate-y-1/2 px-4.5 flex justify-center">
			<button
				type="button"
				class="relative flex bg-white rounded-full gap-4 items-center justify-center shadow h-12 px-4 w-full max-w-80"
				:class="{'pr-18': hasDate}"
				@click="showDateModal = true"
			>
				<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="flex-none">
					<path
						fill-rule="evenodd"
						clip-rule="evenodd"
						d="M20 3.5H17.75V2.75C17.75 2.33579 17.4142 2 17 2C16.5858 2 16.25 2.33579 16.25 2.75V3.5H8.75V2.75C8.75 2.33579 8.41421 2 8 2C7.58579 2 7.25 2.33579 7.25 2.75V3.5H5C4.17157 3.5 3.5 4.17157 3.5 5V20C3.5 20.8284 4.17157 21.5 5 21.5H20C20.8284 21.5 21.5 20.8284 21.5 20V5C21.5 4.17157 20.8284 3.5 20 3.5ZM7.25 5V5.75C7.25 6.16421 7.58579 6.5 8 6.5C8.41421 6.5 8.75 6.16421 8.75 5.75V5H16.25V5.75C16.25 6.16421 16.5858 6.5 17 6.5C17.4142 6.5 17.75 6.16421 17.75 5.75V5H20V8H5V5H7.25ZM5 20H20V9.5H5V20Z"
						fill="#222222"
					/>
				</svg>

				<span class="uppercase">
					{{ text }}
				</span>

				<span
					v-if="hasDate"
					class="absolute z-10 right-4 flex items-center justify-center rounded-full bg-neutral-50 border border-neutral-100 w-6 h-6 flex-none"
					@click.stop="onReset"
				>
					<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M4.93353 3.9998L6.86686 2.06647C6.98908 1.94425 7.0502 1.78869 7.0502 1.5998C7.0502 1.41091 6.98908 1.25536 6.86686 1.13314C6.74464 1.01092 6.58909 0.949804 6.4002 0.949804C6.21131 0.949804 6.05575 1.01092 5.93353 1.13314L4.0002 3.06647L2.06686 1.13314C1.94464 1.01092 1.78908 0.949804 1.6002 0.949804C1.41131 0.949804 1.25575 1.01092 1.13353 1.13314C1.01131 1.25536 0.950195 1.41091 0.950195 1.5998C0.950195 1.78869 1.01131 1.94425 1.13353 2.06647L3.06686 3.9998L1.13353 5.93314C1.01131 6.05536 0.950195 6.21092 0.950195 6.3998C0.950195 6.58869 1.01131 6.74425 1.13353 6.86647C1.25575 6.98869 1.41131 7.0498 1.6002 7.0498C1.78908 7.0498 1.94464 6.98869 2.06686 6.86647L4.0002 4.93314L5.93353 6.86647C6.05575 6.98869 6.21131 7.0498 6.4002 7.0498C6.58909 7.0498 6.74464 6.98869 6.86686 6.86647C6.98908 6.74425 7.0502 6.58869 7.0502 6.3998C7.0502 6.21092 6.98908 6.05536 6.86686 5.93314L4.93353 3.9998Z"
							fill="#222222"
						/>
					</svg>
				</span>
			</button>
		</div>

		<DateModal
			v-if="showDateModal"
			v-model="selectedDates"
			:workspace-id="workspaceId"
			@clear="onClear"
			@close="showDateModal = false"
			@select="onSelect"
		/>
	</div>
</template>

<script setup lang="ts">
import DateModal from "./DateModal.vue";
import {computed, ref, watch} from "vue";
import {useLocalizedDate} from "../useLocalizedDate";
import {useI18n} from "vue-i18n";

const { localizeDate } = useLocalizedDate();
const { t } = useI18n();

export type Props = {
  modelValue: string,
  workspaceId?: number,
  label?: string
}
const props = defineProps<Props>();

const emit = defineEmits([
  'close',
  'select',
  'update:modelValue'
]);

const selectedDates = ref<string[]>([]);
const showDateModal = ref(false);
const hasDate = computed(() => selectedDates.value.length > 0 && selectedDates.value[0] !== 'none')
const text = computed(() => {
  if(!hasDate.value) return props.label ?? t('choose_date');

  return localizeDate(selectedDates.value[0] as string, 'dd - MM - yyyy')
})

const onSelect = () => {
  emit('select', selectedDates.value[0]);
  emit('update:modelValue', selectedDates.value[0]);
}

const onReset = () => {
  selectedDates.value = [];
  emit('update:modelValue', 'none');
}

const onClear = () => {
  onReset();
  showDateModal.value = false;
}

watch(() => props.modelValue, (newValue) => {
  if(newValue !== 'none') {
    selectedDates.value = [newValue];
  } else {
    selectedDates.value = [];
  }
}, { immediate: true });
</script>
